/**
 * Featured Products (New Arrivals)
 * - Hover on a card auto-cycles through its product images.
 * - Cards fade + rise into view on load/scroll (a plain scroll reveal —
 *   every card animates the same way, just staggered slightly per
 *   column so a row cascades in rather than popping in all at once).
 * - Add to Cart adds the card's default variant via the AJAX Cart API.
 * - Load More fetches the next page of products via Shopify's Section
 *   Rendering API and appends the new cards.
 */
(function () {
  function initFeaturedProducts(root) {
    if (!root || root.dataset.fpInitialized === 'true') return;
    root.dataset.fpInitialized = 'true';

    var grid = root.querySelector('[data-product-grid]');
    var hoverEnabled = root.dataset.hoverAutoscroll === 'true';
    var hoverSpeed = parseInt(root.dataset.hoverSpeed, 10) || 700;
    var animationEnabled = root.dataset.enableAnimation === 'true';
    var perRowDesktop = parseInt(root.dataset.perRowDesktop, 10) || 5;
    var perRowMobile = parseInt(root.dataset.perRowMobile, 10) || 2;

    /* ---------------- Hover image autoscroll (smooth, continuous) ---------------- */
    function setupHoverCycle(card) {
      if (!hoverEnabled) return;
      var media = card.querySelector('[data-product-media]');
      var track = card.querySelector('[data-product-track]');
      if (!media || !track || track.dataset.fpTrackBound === 'true') return;
      track.dataset.fpTrackBound = 'true';

      var originalImages = track.querySelectorAll('[data-product-image]');
      if (originalImages.length < 2) return; // nothing to scroll through

      // Clone the first image and append it, so the strip can loop seamlessly
      // (scrolling past the real last image lands on a visual duplicate of
      // the first, then we silently snap back to position 0).
      var firstClone = originalImages[0].cloneNode(true);
      firstClone.removeAttribute('data-product-image');
      firstClone.setAttribute('aria-hidden', 'true');
      track.appendChild(firstClone);

      var originalCount = originalImages.length;
      var position = 0; // px scrolled so far
      var lastTimestamp = null;
      var rafId = null;
      var isHovering = false;

      function step(timestamp) {
        if (lastTimestamp === null) lastTimestamp = timestamp;
        var delta = timestamp - lastTimestamp;
        lastTimestamp = timestamp;

        var containerWidth = media.offsetWidth;
        var wrapPoint = containerWidth * originalCount;

        position += (hoverSpeed * delta) / 1000;
        if (position >= wrapPoint) {
          position -= wrapPoint;
        }

        track.style.transform = 'translateX(-' + position + 'px)';

        if (isHovering) {
          rafId = requestAnimationFrame(step);
        }
      }

      media.addEventListener('mouseenter', function () {
        isHovering = true;
        lastTimestamp = null;
        track.style.transition = 'none';
        if (!rafId) rafId = requestAnimationFrame(step);
      });

      media.addEventListener('mouseleave', function () {
        isHovering = false;
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        position = 0;
        track.style.transition = 'transform 0.4s ease';
        track.style.transform = 'translateX(0)';
      });
    }

    /* ---------------- Entrance animation (scroll reveal) ---------------- */
    var observer = null;

    function currentPerRow() {
      return window.innerWidth <= 749 ? perRowMobile : perRowDesktop;
    }

    function assignAnimationState(card) {
      var index = parseInt(card.dataset.cardIndex, 10) || 0;
      var perRow = currentPerRow();
      var colInRow = index % perRow;

      card.classList.remove('fp-in-view');
      card.classList.add('fp-animate-ready');
      card.style.transitionDelay = (colInRow * getStaggerMs()) + 'ms';
    }

    function getStaggerMs() {
      var val = getComputedStyle(root).getPropertyValue('--fp-anim-stagger');
      return parseInt(val, 10) || 0;
    }

    function observeCard(card) {
      if (!animationEnabled) {
        card.classList.remove('fp-animate-ready');
        return;
      }
      assignAnimationState(card);
      if (observer) observer.observe(card);
    }

    function createObserver() {
      if (!('IntersectionObserver' in window)) return null;
      return new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('fp-in-view');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
      );
    }

    function recalculateAllAnimationStates() {
      if (!animationEnabled) return;
      grid.querySelectorAll('[data-product-card]').forEach(function (card) {
        // Only recompute stagger for cards not yet animated in
        if (!card.classList.contains('fp-in-view')) {
          assignAnimationState(card);
        }
      });
    }

    /* ---------------- Add to cart ---------------- */
    function setupAddToCart(card) {
      var btn = card.querySelector('[data-add-to-cart]');
      if (!btn || btn.dataset.fpBound === 'true') return;
      btn.dataset.fpBound = 'true';

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var variantId = btn.dataset.variantId;
        if (!variantId) return;

        var textEl = btn.querySelector('[data-add-btn-text]');
        var originalText = textEl ? textEl.textContent : '';
        btn.disabled = true;
        if (textEl) textEl.textContent = 'Adding...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('Add to cart failed');
            return res.json();
          })
          .then(function () {
            btn.classList.add('is-added');
            if (textEl) textEl.textContent = 'Added!';
            document.dispatchEvent(new CustomEvent('cart:updated'));
            setTimeout(function () {
              btn.classList.remove('is-added');
              if (textEl) textEl.textContent = originalText;
              btn.disabled = false;
            }, 1800);
          })
          .catch(function () {
            if (textEl) textEl.textContent = 'Error';
            setTimeout(function () {
              if (textEl) textEl.textContent = originalText;
              btn.disabled = false;
            }, 1800);
          });
      });
    }

    function initCard(card) {
      setupHoverCycle(card);
      setupAddToCart(card);
      observeCard(card);
    }

    observer = createObserver();
    grid.querySelectorAll('[data-product-card]').forEach(initCard);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(recalculateAllAnimationStates, 150);
    });

    /* ---------------- Load more ---------------- */
    var loadMoreWrap = root.querySelector('[data-load-more-wrap]');
    var loadMoreBtn = root.querySelector('[data-load-more-btn]');
    if (loadMoreBtn) {
      var totalPages = parseInt(root.dataset.totalPages, 10) || 1;
      var sectionId = root.dataset.sectionId;
      var collectionUrl = root.dataset.collectionUrl;

      loadMoreBtn.addEventListener('click', function () {
        var nextPage = parseInt(loadMoreBtn.dataset.nextPage, 10) || 2;
        if (nextPage > totalPages) {
          if (loadMoreWrap) loadMoreWrap.hidden = true;
          return;
        }

        var textEl = loadMoreBtn.querySelector('[data-load-more-text]');
        var originalText = textEl ? textEl.textContent : '';
        loadMoreBtn.disabled = true;
        if (textEl) textEl.textContent = 'Loading...';

        var separator = collectionUrl.indexOf('?') > -1 ? '&' : '?';
        var fetchUrl = collectionUrl + separator + 'page=' + nextPage + '&section_id=' + sectionId;

        fetch(fetchUrl)
          .then(function (res) {
            if (!res.ok) throw new Error('Load more failed');
            return res.text();
          })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var newGrid = doc.querySelector('[data-product-grid]');
            if (!newGrid) return;

            var existingCount = grid.querySelectorAll('[data-product-card]').length;
            var newCards = newGrid.querySelectorAll('[data-product-card]');

            newCards.forEach(function (card, i) {
              card.dataset.cardIndex = existingCount + i;
              grid.appendChild(card);
              initCard(card);
            });

            nextPage += 1;
            loadMoreBtn.dataset.nextPage = nextPage;

            if (nextPage > totalPages) {
              if (loadMoreWrap) loadMoreWrap.hidden = true;
            }

            loadMoreBtn.disabled = false;
            if (textEl) textEl.textContent = originalText;
          })
          .catch(function () {
            loadMoreBtn.disabled = false;
            if (textEl) textEl.textContent = originalText;
          });
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-featured-products]').forEach(initFeaturedProducts);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-featured-products]');
    if (root) initFeaturedProducts(root);
  });
})();